import { type BookChapter, type Position } from './book';

// TTS gives no precise timing, so time-skips are estimated from an average speaking rate. The skip
// distance scales with playback speed: at 2x, 15 seconds of audio covers twice as many words.
export const WORDS_PER_MINUTE_BASE = 150;
export const SKIP_SECONDS = 15;

export function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed === '' ? 0 : trimmed.split(/\s+/).length;
}

function wordsPerSkip(speed: number): number {
  return ((WORDS_PER_MINUTE_BASE * speed) / 60) * SKIP_SECONDS;
}

function lastParagraph(chapters: readonly BookChapter[], chapter: number): number {
  return Math.max(0, chapters[chapter].paragraphs.length - 1);
}

/** Clamp a position to a valid (chapter, paragraph) pair for the given book. */
export function clampPosition(chapters: readonly BookChapter[], pos: Position): Position {
  if (chapters.length === 0) return { chapter: 0, paragraph: 0 };
  const chapter = Math.min(Math.max(0, pos.chapter), chapters.length - 1);
  const paragraph = Math.min(Math.max(0, pos.paragraph), lastParagraph(chapters, chapter));
  return { chapter, paragraph };
}

/** The next paragraph, crossing into the next chapter; null when already at the very end. */
export function nextParagraph(chapters: readonly BookChapter[], pos: Position): Position | null {
  if (chapters.length === 0) return null;
  if (pos.paragraph + 1 < chapters[pos.chapter].paragraphs.length) {
    return { chapter: pos.chapter, paragraph: pos.paragraph + 1 };
  }
  if (pos.chapter + 1 < chapters.length) {
    return { chapter: pos.chapter + 1, paragraph: 0 };
  }
  return null;
}

/** Move forward ~15 seconds of audio, crossing chapter boundaries. Clamps at the end of the book. */
export function skipForward(
  chapters: readonly BookChapter[],
  pos: Position,
  speed: number,
): Position {
  if (chapters.length === 0) return { chapter: 0, paragraph: 0 };
  let remaining = wordsPerSkip(speed);
  let { chapter, paragraph } = clampPosition(chapters, pos);

  while (remaining > 0) {
    remaining -= countWords(chapters[chapter].paragraphs[paragraph]);
    const next = nextParagraph(chapters, { chapter, paragraph });
    if (!next) return { chapter, paragraph };
    chapter = next.chapter;
    paragraph = next.paragraph;
  }
  return { chapter, paragraph };
}

/** Move backward ~15 seconds of audio, crossing chapter boundaries. Clamps at the start. */
export function skipBackward(
  chapters: readonly BookChapter[],
  pos: Position,
  speed: number,
): Position {
  if (chapters.length === 0) return { chapter: 0, paragraph: 0 };
  let remaining = wordsPerSkip(speed);
  let { chapter, paragraph } = clampPosition(chapters, pos);

  while (remaining > 0) {
    if (paragraph > 0) {
      paragraph--;
    } else if (chapter > 0) {
      chapter--;
      paragraph = lastParagraph(chapters, chapter);
    } else {
      return { chapter: 0, paragraph: 0 };
    }
    remaining -= countWords(chapters[chapter].paragraphs[paragraph]);
  }
  return { chapter, paragraph };
}

/** 0-100, how far through the current chapter the position sits. */
export function chapterProgressPercent(
  chapters: readonly BookChapter[],
  pos: Position,
): number {
  const chapter = chapters[pos.chapter];
  if (!chapter || chapter.paragraphs.length === 0) return 0;
  return Math.round((pos.paragraph / chapter.paragraphs.length) * 100);
}
